import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useTranslations } from '@/i18n/translations';
import { LoadingState } from '@leanin/ui';
import { EntryJourneyStepper } from '@/components/entry-journeys';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import { PROFILE_FORM_ID, ProfileStep } from '@/components/onboarding/ProfileStep';
import { StepFooter } from '@/components/onboarding/StepFooter';
import { readEntryJourney } from '@/lib/entry-journey';
import { readPendingInviteToken } from '@/components/onboarding/pending-invite';
import { useMe } from '@/lib/queries/identity';

export const Route = createFileRoute('/onboarding/profile')({
  // A journey user bounced here by a generic gate (e.g. hitting an authed
  // URL mid-onboarding) belongs in the journey steps, not the organic
  // profile. Invites outrank journeys — their persona preset lives here.
  beforeLoad: () => {
    if (readEntryJourney() !== null && readPendingInviteToken() === null) {
      throw redirect({ to: '/onboarding/j/about' });
    }
  },
  component: OnboardingProfilePage,
});

function OnboardingProfilePage() {
  const t = useTranslations('Onboarding');
  const { data, isError, isPending } = useMe();
  // Ad-journey funnel position: the profile step is "About you". Read once at
  // mount (established sessionStorage pattern); null for organic signups, so
  // the shell renders without the stepper slot, exactly as before.
  const [journey] = useState(readEntryJourney);
  // Mirror the old server page: swallow fetch errors and hand `undefined`
  // to ProfileStep so it falls back to defaults. The step mounts only once
  // the query settles: its fields are uncontrolled and ignore a
  // `defaultValue` that arrives after mount.
  //
  // `place` hydration: ProfileSelfResponse still exposes the legacy flat
  // `location` field via OpenAPI (server view migration is Wave 2). With no
  // placeId/lat/lng available, we can't hydrate a real `PlaceSelection` —
  // the picker starts empty and the user re-picks. The backfill agent's
  // migration covers existing DB rows server-side.
  const initialProfile =
    data !== undefined && !isError
      ? {
          firstName: data.firstName,
          lastName: data.lastName ?? '',
          jobTitle: data.jobTitle ?? '',
          industryCode: data.industryCode ?? null,
          place: null,
          ...(data.photoUrl !== undefined ? { photoUrl: data.photoUrl } : {}),
          photoOriginalUrl: data.photoOriginalUrl,
          photoCrop: data.photoCrop,
        }
      : undefined;

  return (
    <OnboardingShell
      title={t('profile.shellTitle')}
      footerHint={t('shell.footerHint')}
      {...(journey !== null
        ? {
            stepper: (
              <EntryJourneyStepper journey={journey} current="about-you" />
            ),
          }
        : {})}
      footer={
        <StepFooter
          formId={PROFILE_FORM_ID}
          primaryLabel={t('profile.continue')}
          pendingLabel={t('profile.saving')}
        />
      }
    >
      {isPending ? (
        <LoadingState placement="inset" label={t('profile.loading')} />
      ) : (
        <ProfileStep initialProfile={initialProfile} />
      )}
    </OnboardingShell>
  );
}
